import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ActionIcon, Box, createStyles, Icon, TFNotifier, TFText, Tooltip } from '@tf/ui';
import type React from 'react';
import type { CaseRequestFile } from '@/core/api';
import { useDownloadCaseRequestFileMutation } from '@/core/api';

const useStyles = createStyles(({ colors, radius }) => ({
	wrapper: {
		display: 'flex',
		width: '100%',
		alignItems: 'center',
		justifyContent: 'space-between',
		padding: '.25rem .75rem',
		borderRadius: radius.sm,
		backgroundColor: colors.gray[1],
		marginTop: '.5rem',
	},
	content: {
		flex: 1,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		marginRight: '.5rem',
	},
}));

interface RequestFileListProps {
	requestId: string;
	files: CaseRequestFile[];
}

export const RequestFileList: React.FC<RequestFileListProps> = ({ requestId, files }) => {
	const { _ } = useLingui();
	const { classes } = useStyles();

	const downloadFileMutation = useDownloadCaseRequestFileMutation();

	const downloadFile = (file: CaseRequestFile) => {
		downloadFileMutation.mutate(
			{ requestId, file },
			{
				onError: () => {
					TFNotifier.error(_(msg({ id: 'caseRequestPage.downloadFileError', message: 'Failed to download the file' })));
				},
			},
		);
	};

	return (
		<Box mt="1rem">
			<TFText size="md" fw="600">
				<Trans id="caseRequestPage.requestFilesTitle">Files attached to the request:</Trans>
			</TFText>
			{files.map((file) => (
				<Box key={file.id} className={classes.wrapper}>
					<Box className={classes.content}>{file.filename}</Box>
					<Tooltip label={_(msg({ id: 'caseRequestPage.downloadFileTooltip', message: 'Download file' }))} withArrow>
						<ActionIcon
							variant="subtle"
							loading={downloadFileMutation.isPending && downloadFileMutation.variables?.file.id === file.id}
							onClick={() => downloadFile(file)}
						>
							<Icon.IconDownload size={18} stroke={1.5} />
						</ActionIcon>
					</Tooltip>
				</Box>
			))}
		</Box>
	);
};
